import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/auth/AuthContext'
import ParentAccessModal from './ParentAccessModal'

// ─── API layer ────────────────────────────────────────────────────────────────
async function apiFetch(method, path, body, headers) {
  const h = { 'Content-Type': 'application/json', ...(headers || {}) }
  let res
  try { res = await fetch(`/api${path}`, { method, headers: h, body: body ? JSON.stringify(body) : undefined }) }
  catch { throw new Error('Network error — check your connection.') }
  let data
  try { const t = await res.text(); data = t ? JSON.parse(t) : {} }
  catch { throw new Error(`Non-JSON response (HTTP ${res.status})`) }
  if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`)
  return data
}

function useApi() {
  const { getAuthHeader } = useAuth()
  return useCallback((method, path, body) => apiFetch(method, path, body, getAuthHeader()), [getAuthHeader])
}

const pickArray = (data, ...keys) => {
  for (const k of keys) if (Array.isArray(data?.[k])) return data[k]
  return Array.isArray(data) ? data : []
}

// ─── Progress helpers ─────────────────────────────────────────────────────────
const DOMAIN_IDS    = ['physical-motor','socio-emotional','cognitive','language-literacy','numeracy']
const DOMAIN_SHORT  = { 'physical-motor':'Physical','socio-emotional':'Social','cognitive':'Cognitive','language-literacy':'Language','numeracy':'Numeracy' }
const DOMAIN_COLORS = { 'physical-motor':'#1B6B45','socio-emotional':'#C0392B','cognitive':'#6B2D8B','language-literacy':'#1A4A6E','numeracy':'#D4AC0D' }
const LEVEL_SCORE   = { 'not-observed':0,'N/O':0,emerging:33,E:33,developing:66,D:66,achieved:100,A:100 }

function domainScores(indicators) {
  if (!Array.isArray(indicators) || !indicators.length) return {}
  const s = {}
  for (const d of DOMAIN_IDS) {
    const inds = indicators.filter(i => i.domainId === d)
    s[d] = inds.length ? Math.round(inds.reduce((a,i) => a+(LEVEL_SCORE[i.current?.level||'not-observed']??0),0)/inds.length) : null
  }
  return s
}
function overallScore(scores) {
  const v = Object.values(scores).filter(x => x !== null)
  return v.length ? Math.round(v.reduce((a,b)=>a+b,0)/v.length) : null
}
function scoreColor(p) { return p>=80?'#1B6B45':p>=50?'#D4AC0D':'#C0392B' }

// ─── Subscription constants ───────────────────────────────────────────────────
const PLANS = [
  { id:'trial',    label:'Trial',    color:'#7B2D8B', bg:'#F5EEF8', teachers:2,   students:10,  price:'Free / 30 days' },
  { id:'basic',    label:'Basic',    color:'#1A4A6E', bg:'#EBF4FB', teachers:5,   students:20,  price:'₹12,000/yr' },
  { id:'standard', label:'Standard', color:'#1B6B45', bg:'#EDF7F2', teachers:15,  students:100, price:'₹30,000/yr' },
  { id:'premium',  label:'Premium',  color:'#C0392B', bg:'#FDEDEC', teachers:999, students:999, price:'₹60,000/yr' },
]
const PLAN_PRICES  = { trial:0, basic:12000, standard:30000, premium:60000 }
const SUB_STATUSES = ['trial','active','expired','cancelled','suspended']
const SUB_META = {
  trial:     { color:'#7B2D8B', bg:'#F5EEF8', dot:'🟣', label:'Trial'     },
  active:    { color:'#1B6B45', bg:'#EDF7F2', dot:'🟢', label:'Active'    },
  expired:   { color:'#C0392B', bg:'#FDEDEC', dot:'🔴', label:'Expired'   },
  cancelled: { color:'#9E9E9E', bg:'#F5F5F5', dot:'⚫', label:'Cancelled' },
  suspended: { color:'#E65100', bg:'#FFF3E0', dot:'🟠', label:'Suspended' },
}

// ─── UI atoms ─────────────────────────────────────────────────────────────────
const IS = { width:'100%', padding:'9px 12px', borderRadius:8, boxSizing:'border-box', border:'1.5px solid var(--border)', fontSize:13, color:'var(--ink)', background:'white', outline:'none' }

function Input({ value, onChange, placeholder, type='text' }) {
  return <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={IS} />
}
function Select({ value, onChange, options, placeholder }) {
  return (
    <select value={value} onChange={e=>onChange(e.target.value)} style={IS}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  )
}
function Field({ label, children, required }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:10, fontWeight:700, color:'var(--mist)', marginBottom:5, textTransform:'uppercase', letterSpacing:0.7 }}>
        {label}{required && <span style={{ color:'#E53935', marginLeft:2 }}>*</span>}
      </label>
      {children}
    </div>
  )
}
function Btn({ children, onClick, variant='primary', small, disabled }) {
  const S = {
    primary:   { background:'var(--navy)', color:'white', border:'none' },
    secondary: { background:'white', color:'var(--ink)', border:'1.5px solid var(--border)' },
    danger:    { background:'#FDEDEC', color:'#C0392B', border:'1.5px solid #F1948A' },
    ghost:     { background:'transparent', color:'var(--navy)', border:'1.5px solid var(--navy)' },
  }
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...S[variant], padding:small?'5px 12px':'8px 18px', borderRadius:8, fontSize:small?11:12, fontWeight:700,
               cursor:disabled?'not-allowed':'pointer', opacity:disabled?0.5:1, whiteSpace:'nowrap' }}>
      {children}
    </button>
  )
}
function Badge({ label, color='#666', bg='#eee', dot }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10, fontWeight:700, color, background:bg,
                   padding:'3px 9px', borderRadius:20, whiteSpace:'nowrap' }}>
      {dot && <span style={{ fontSize:8 }}>{dot}</span>}{label}
    </span>
  )
}
function Avatar({ name='', color='#1A4A6E', size=36 }) {
  const ini = name.trim().split(/\s+/).map(w=>w[0]||'').join('').slice(0,2).toUpperCase()||'?'
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:color, color:'white',
                  fontWeight:900, fontSize:size*0.35, display:'flex', alignItems:'center',
                  justifyContent:'center', flexShrink:0, letterSpacing:-0.5 }}>
      {ini}
    </div>
  )
}
function Modal({ title, subtitle, onClose, children, width=520 }) {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center',
                  background:'rgba(10,20,40,0.55)', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'white', borderRadius:16, width, maxWidth:'96vw', maxHeight:'92vh',
                    display:'flex', flexDirection:'column', boxShadow:'0 24px 70px rgba(0,0,0,.25)', animation:'fadeUp .18s ease' }}>
        <div style={{ padding:'18px 24px 14px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12 }}>
            <div>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:800, color:'var(--navy)' }}>{title}</div>
              {subtitle && <div style={{ fontSize:11, color:'var(--mist)', marginTop:3 }}>{subtitle}</div>}
            </div>
            <button onClick={onClose}
              style={{ background:'var(--fog)', border:'none', borderRadius:'50%', width:30, height:30,
                       cursor:'pointer', fontSize:14, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
              ✕
            </button>
          </div>
        </div>
        <div style={{ overflowY:'auto', padding:'20px 24px', flex:1 }}>{children}</div>
      </div>
    </div>
  )
}
function Spinner() {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:60, gap:12 }}>
      <div style={{ width:24, height:24, border:'3px solid var(--fog)', borderTop:'3px solid var(--navy)', borderRadius:'50%', animation:'spin .7s linear infinite' }} />
      <div style={{ fontSize:12, color:'var(--mist)' }}>Loading…</div>
    </div>
  )
}
function Empty({ icon, title, sub }) {
  return (
    <div style={{ textAlign:'center', padding:'52px 0', color:'var(--mist)' }}>
      <div style={{ fontSize:36, marginBottom:10 }}>{icon}</div>
      <div style={{ fontSize:14, fontWeight:700, color:'var(--navy)', marginBottom:4 }}>{title}</div>
      {sub && <div style={{ fontSize:12 }}>{sub}</div>}
    </div>
  )
}
function ErrPane({ msg, onRetry }) {
  return (
    <div style={{ background:'#FEF9F9', border:'1.5px solid #F1948A', borderRadius:12, padding:'18px 20px', display:'flex', gap:14 }}>
      <span style={{ fontSize:22 }}>⚠️</span>
      <div>
        <div style={{ fontWeight:700, fontSize:13, color:'#922B21', marginBottom:4 }}>Failed to load data</div>
        <div style={{ fontSize:12, color:'#922B21', lineHeight:1.6, marginBottom:onRetry?10:0 }}>{msg}</div>
        {onRetry && <Btn small variant="danger" onClick={onRetry}>↺ Retry</Btn>}
      </div>
    </div>
  )
}
function ErrMsg({ msg }) {
  if (!msg) return null
  return <div style={{ background:'#FDEDEC', color:'#C0392B', padding:'9px 12px', borderRadius:8, fontSize:12, marginBottom:14, fontWeight:600 }}>{msg}</div>
}
function Toast({ msg }) {
  if (!msg) return null
  return (
    <div style={{ position:'fixed', bottom:28, right:28, zIndex:2000, background:'#1a1a2e', color:'white',
                  padding:'11px 22px', borderRadius:10, fontSize:13, fontWeight:700,
                  boxShadow:'0 8px 28px rgba(0,0,0,.25)', animation:'fadeUp .2s ease' }}>
      {msg}
    </div>
  )
}
function Divider({ label }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:10, margin:'18px 0 12px' }}>
      <div style={{ flex:1, height:1, background:'var(--border)' }} />
      <div style={{ fontSize:10, fontWeight:700, color:'var(--mist)', textTransform:'uppercase', letterSpacing:0.8 }}>{label}</div>
      <div style={{ flex:1, height:1, background:'var(--border)' }} />
    </div>
  )
}

// ── Seat Bar ──────────────────────────────────────────────────────────────────
function SeatBar({ used, max, label, icon }) {
  if (max == null) return null
  const isUnlimited = max === 999
  const pct         = isUnlimited ? 0 : Math.min(100, Math.round((used / max) * 100))
  const atLimit     = !isUnlimited && used >= max
  const nearLimit   = !isUnlimited && pct >= 80 && !atLimit
  const barColor    = atLimit ? '#C0392B' : nearLimit ? '#E65100' : '#1B6B45'
  const borderColor = atLimit ? '#F1948A' : nearLimit ? '#FFCBA4' : 'var(--border)'
  return (
    <div style={{ background:'white', borderRadius:10, border:`1.5px solid ${borderColor}`, padding:'10px 14px', marginBottom:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: isUnlimited ? 0 : 8 }}>
        <span style={{ fontSize:14 }}>{icon}</span>
        <span style={{ fontWeight:800, fontSize:12, color:'var(--navy)' }}>{label} quota</span>
        {isUnlimited
          ? <Badge label="Unlimited" color="#1B6B45" bg="#EDF7F2" />
          : <span style={{ fontSize:12, fontWeight:700, color:barColor }}>{used} / {max} used</span>
        }
        {atLimit && <Badge label="🔒 Limit reached" color="#C0392B" bg="#FDEDEC" />}
      </div>
      {!isUnlimited && (
        <>
          <div style={{ height:6, background:'var(--fog)', borderRadius:3 }}>
            <div style={{ width:`${pct}%`, height:'100%', background:barColor, borderRadius:3, transition:'width .4s' }} />
          </div>
          {nearLimit && !atLimit && <div style={{ marginTop:5, fontSize:11, color:'#E65100', fontWeight:600 }}>⚠️ {max - used} slot{max-used!==1?'s':''} remaining</div>}
          {atLimit && <div style={{ marginTop:5, fontSize:11, color:'#922B21' }}>Contact NISH to upgrade your subscription plan.</div>}
        </>
      )}
    </div>
  )
}

// ─── Subscription status banner ───────────────────────────────────────────────
function SubBanner({ sub }) {
  if (!sub) {
    return (
      <div style={{ background:'#F5F5F5', border:'1.5px solid #E0E0E0', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontSize:20 }}>📋</span>
        <div>
          <div style={{ fontWeight:700, fontSize:13, color:'#757575' }}>No Subscription</div>
          <div style={{ fontSize:11, color:'#9E9E9E' }}>Contact NISH to activate a plan for this school.</div>
        </div>
      </div>
    )
  }
  const sm = SUB_META[sub.status] || SUB_META.trial
  const plan = PLANS.find(p => p.id === sub.plan)
  const daysLeft = sub.validUntil ? Math.ceil((new Date(sub.validUntil) - new Date()) / 86400000) : null
  return (
    <div style={{ background:sm.bg, border:`2px solid ${sm.color}30`, borderRadius:12, padding:'14px 18px', display:'flex', gap:14, alignItems:'flex-start' }}>
      <div style={{ fontSize:28, lineHeight:1 }}>{sm.dot}</div>
      <div style={{ flex:1 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:5 }}>
          <span style={{ fontWeight:800, fontSize:15, color:sm.color }}>{sm.label}</span>
          {plan && <Badge label={plan.label} color={plan.color} bg="white" />}
          {daysLeft !== null && daysLeft > 0 && daysLeft <= 30 && <Badge label={`⚠️ ${daysLeft} days left`} color="#E65100" bg="#FFF3E0" />}
          {daysLeft !== null && daysLeft <= 0 && sub.status === 'active' && <Badge label="⚠️ Expired" color="#C0392B" bg="#FDEDEC" />}
        </div>
        <div style={{ fontSize:11, color:sm.color, opacity:0.8, lineHeight:1.7 }}>
          {plan && `${plan.teachers===999?'∞':plan.teachers} teachers · ${plan.students===999?'∞':plan.students} students`}
          {sub.validFrom  && ` · From ${new Date(sub.validFrom).toLocaleDateString('en-IN')}`}
          {sub.validUntil && ` · Until ${new Date(sub.validUntil).toLocaleDateString('en-IN')}`}
          {sub.annualFee > 0 && ` · ₹${(+sub.annualFee).toLocaleString('en-IN')}/yr`}
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// SCHOOL PICKER BANNER — shown at top of scoped tabs for superadmin
// ══════════════════════════════════════════════════════════════════════════════
function SchoolPicker({ schools, selectedId, onChange }) {
  const selected = schools.find(s => s._id === selectedId)
  const sub = selected?.subscription
  const sm  = SUB_META[sub?.status || 'trial']

  return (
    <div style={{ background:'white', borderRadius:12, border:'1.5px solid var(--border)', padding:'14px 18px', marginBottom:20, display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
      <span style={{ fontSize:16 }}>🏫</span>
      <div style={{ fontWeight:700, fontSize:13, color:'var(--navy)', flexShrink:0 }}>Viewing school:</div>
      <select
        value={selectedId || ''}
        onChange={e => onChange(e.target.value)}
        style={{ flex:1, minWidth:200, padding:'8px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:13, outline:'none', background:'white', fontWeight:600, color:'var(--ink)' }}>
        <option value="">— Select a school —</option>
        {schools.map(s => (
          <option key={s._id} value={s._id}>{s.name}{s.city ? ` · ${s.city}` : ''}</option>
        ))}
      </select>
      {selected && (
        <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
          <span style={{ fontSize:11, color:sm.color, fontWeight:700 }}>{sm.dot} {sm.label}</span>
          {sub?.plan && <Badge label={PLANS.find(p=>p.id===sub.plan)?.label||sub.plan} color={sm.color} bg={sm.bg} />}
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// SCHOOLS TAB
// ══════════════════════════════════════════════════════════════════════════════
function SchoolsTab() {
  const api = useApi()
  const [schools,  setSchools]  = useState([])
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState('')
  const [showAdd,  setShowAdd]  = useState(false)
  const [selected, setSelected] = useState(null)
  const [search,   setSearch]   = useState('')
  const [toast,    setToast]    = useState('')
  const toast$ = msg => { setToast(msg); setTimeout(()=>setToast(''), 3200) }

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const d = await api('GET', '/schools')
      const list = pickArray(d, 'schools', 'data', 'items')
      list.sort((a,b) => new Date(a.createdAt||0) - new Date(b.createdAt||0))
      setSchools(list)
    } catch(e) { setError(e.message) }
    finally    { setLoading(false) }
  }, [api])

  useEffect(() => { load() }, [load])

  const filtered = schools.filter(s =>
    !search ||
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    s.city?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18 }}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍  Search schools…"
          style={{ flex:1, padding:'8px 13px', border:'1.5px solid var(--border)', borderRadius:9, fontSize:13, outline:'none' }} />
        <Btn onClick={()=>setShowAdd(true)}>+ Add School</Btn>
      </div>

      {loading ? <Spinner /> :
       error   ? <ErrPane msg={error} onRetry={load} /> :
       filtered.length === 0
         ? <Empty icon="🏫" title="No schools yet" sub="Add your first school to get started." />
         : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filtered.map(s => <SchoolRow key={s._id} school={s} onManage={()=>setSelected(s)} />)}
          </div>
        )
      }

      {showAdd  && <AddSchoolModal onClose={()=>setShowAdd(false)} onSaved={()=>{ setShowAdd(false); load(); toast$('✅ School created') }} onError={msg=>toast$(`⚠️ ${msg}`)} />}
      {selected && <SchoolDetailModal school={selected} onClose={()=>{ setSelected(null); load() }} toast$={toast$} />}
      <Toast msg={toast} />
    </div>
  )
}

function SchoolRow({ school: s, onManage }) {
  const sub  = s.subscription
  const sm   = SUB_META[sub?.status || 'trial']
  const plan = PLANS.find(p => p.id === sub?.plan)
  return (
    <div style={{ background:'white', borderRadius:12, border:'1.5px solid var(--border)', overflow:'hidden' }}>
      <div style={{ display:'flex' }}>
        <div style={{ width:5, background:sm.color, flexShrink:0 }} />
        <div style={{ flex:1, padding:'14px 18px', display:'flex', alignItems:'center', gap:16 }}>
          <div style={{ width:44, height:44, borderRadius:10, background:sm.color, color:'white',
                        display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:15, flexShrink:0 }}>
            {s.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:4 }}>
              <span style={{ fontWeight:800, fontSize:14, color:'var(--navy)' }}>{s.name}</span>
              {!s.isActive && <Badge label="Inactive" color="#9E9E9E" bg="#F5F5F5" />}
            </div>
            <div style={{ fontSize:11, color:'var(--mist)' }}>
              {[s.city, s.state].filter(Boolean).join(', ') || 'No location set'}
              {s.adminUser ? ` · Admin: ${s.adminUser.name}` : ''}
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, flexShrink:0 }}>
            <div style={{ background:sm.bg, border:`1.5px solid ${sm.color}40`, borderRadius:8, padding:'6px 12px', textAlign:'center', minWidth:90 }}>
              <div style={{ fontSize:9, fontWeight:700, color:sm.color, textTransform:'uppercase', letterSpacing:0.6, marginBottom:2 }}>{sm.dot} {sm.label}</div>
              {plan && <div style={{ fontSize:10, fontWeight:800, color:sm.color }}>{plan.label}</div>}
            </div>
            {sub && (
              <div style={{ fontSize:9, color:'var(--mist)', textAlign:'center', lineHeight:1.5 }}>
                {sub.maxTeachers===999?'∞':sub.maxTeachers} teachers · {sub.maxStudents===999?'∞':sub.maxStudents} students
              </div>
            )}
          </div>
          {sub?.validUntil && (() => {
            const days = Math.ceil((new Date(sub.validUntil) - new Date()) / 86400000)
            if (days > 0 && days <= 30) return <Badge label={`⚠️ ${days}d left`} color="#E65100" bg="#FFF3E0" />
            if (days <= 0 && sub.status === 'active') return <Badge label="⚠️ Expired" color="#C0392B" bg="#FDEDEC" />
            return null
          })()}
          <Btn small variant="secondary" onClick={onManage}>Manage →</Btn>
        </div>
      </div>
    </div>
  )
}

// ── Add School Modal ──────────────────────────────────────────────────────────
function AddSchoolModal({ onClose, onSaved, onError }) {
  const api = useApi()
  const [f, setF] = useState({ name:'', city:'', state:'', address:'', phone:'', email:'', plan:'trial', adminName:'', adminEmail:'', adminPassword:'' })
  const [saving, setSaving] = useState(false)
  const set = k => v => setF(p=>({...p,[k]:v}))

  const submit = async () => {
    if (!f.name) { onError('School name is required.'); return }
    setSaving(true)
    try { await api('POST', '/schools', f); onSaved() }
    catch(e) { onError(e.message) }
    finally  { setSaving(false) }
  }

  return (
    <Modal title="Add New School" subtitle="School details and optional admin account" onClose={onClose} width={620}>
      <Divider label="School Info" />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="School Name" required><Input value={f.name} onChange={set('name')} placeholder="e.g. Kendriya Vidyalaya Pattom" /></Field>
        </div>
        <Field label="City"><Input value={f.city} onChange={set('city')} placeholder="Thiruvananthapuram" /></Field>
        <Field label="State"><Input value={f.state} onChange={set('state')} placeholder="Kerala" /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={set('phone')} placeholder="+91 …" /></Field>
        <Field label="Email"><Input type="email" value={f.email} onChange={set('email')} placeholder="school@example.com" /></Field>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Address"><Input value={f.address} onChange={set('address')} placeholder="Full address" /></Field>
        </div>
      </div>
      <Divider label="Initial Subscription Plan" />
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:6 }}>
        {PLANS.map(p => (
          <div key={p.id} onClick={()=>set('plan')(p.id)}
            style={{ border:`2px solid ${f.plan===p.id?p.color:'var(--border)'}`, background:f.plan===p.id?p.bg:'white',
                     borderRadius:9, padding:'10px 8px', cursor:'pointer', textAlign:'center', transition:'all .12s' }}>
            <div style={{ fontWeight:800, fontSize:12, color:p.color }}>{p.label}</div>
            <div style={{ fontSize:9, color:'var(--mist)', marginTop:3 }}>👨‍🏫 {p.teachers===999?'∞':p.teachers}</div>
            <div style={{ fontSize:9, color:'var(--mist)' }}>👤 {p.students===999?'∞':p.students}</div>
            <div style={{ fontSize:10, fontWeight:700, color:p.color, marginTop:3 }}>{p.price}</div>
          </div>
        ))}
      </div>
      <Divider label="Admin Account (optional)" />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
        <Field label="Name"><Input value={f.adminName} onChange={set('adminName')} placeholder="Full name" /></Field>
        <Field label="Email"><Input type="email" value={f.adminEmail} onChange={set('adminEmail')} placeholder="admin@school.com" /></Field>
        <Field label="Password"><Input type="password" value={f.adminPassword} onChange={set('adminPassword')} placeholder="Min 8 chars" /></Field>
      </div>
      <div style={{ display:'flex', gap:9, justifyContent:'flex-end', marginTop:8 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving?'Creating…':'Create School'}</Btn>
      </div>
    </Modal>
  )
}

// ── School Detail Modal ───────────────────────────────────────────────────────
function SchoolDetailModal({ school, onClose, toast$ }) {
  const api = useApi()
  const [tab,    setTab]    = useState('details')
  const [data,   setData]   = useState(school)
  const [sub,    setSub]    = useState(school.subscription || null)
  const [saving, setSaving] = useState(false)

  // Admin tab state
  const [adminData,    setAdminData]    = useState(school.adminUser || null)
  const [newAdminPw,   setNewAdminPw]   = useState('')
  const [newAdminForm, setNewAdminForm] = useState({ name:'', email:'', password:'' })
  const [adminSaving,  setAdminSaving]  = useState(false)

  const saveDetails = async () => {
    setSaving(true)
    try {
      await api('PUT', `/schools/${school._id}`, { name:data.name, city:data.city, state:data.state, address:data.address, phone:data.phone, email:data.email, isActive:data.isActive })
      toast$('✅ School updated')
    } catch(e) { toast$('⚠️ '+e.message) }
    finally    { setSaving(false) }
  }

  const saveSub = async () => {
    setSaving(true)
    try {
      const d = await api('PUT', `/subscriptions/${school._id}`, sub)
      setSub(d.subscription || d.data || d)
      toast$('✅ Subscription updated')
    } catch(e) { toast$('⚠️ '+e.message) }
    finally    { setSaving(false) }
  }

  const activateSub = async () => {
    setSaving(true)
    try {
      const d = await api('PUT', `/subscriptions/${school._id}`, { plan:'trial', status:'trial' })
      setSub(d.subscription || d.data || d)
      toast$('✅ Trial subscription activated')
    } catch(e) { toast$('⚠️ '+e.message) }
    finally    { setSaving(false) }
  }

  const resetAdminPassword = async () => {
    if (!newAdminPw || newAdminPw.length < 8) { toast$('⚠️ Password must be at least 8 characters'); return }
    setAdminSaving(true)
    try {
      await api('POST', `/users/${adminData._id}/reset-password`, { newPassword: newAdminPw })
      setNewAdminPw('')
      toast$('✅ Admin password reset')
    } catch(e) { toast$('⚠️ '+e.message) }
    finally    { setAdminSaving(false) }
  }

  const createAdmin = async () => {
    if (!newAdminForm.name || !newAdminForm.email || !newAdminForm.password) { toast$('⚠️ All fields required'); return }
    if (newAdminForm.password.length < 8) { toast$('⚠️ Password must be at least 8 characters'); return }
    setAdminSaving(true)
    try {
      const d = await api('POST', '/users', {
        name:        newAdminForm.name,
        email:       newAdminForm.email,
        password:    newAdminForm.password,
        role:        'admin',
        school:      school._id,
        isActive:    true,
        isVerified:  true,
        authMethods: ['local'],
      })
      const newAdmin = d.user || d
      // Link admin to school
      await api('PUT', `/schools/${school._id}`, { adminUser: newAdmin._id })
      setAdminData(newAdmin)
      setNewAdminForm({ name:'', email:'', password:'' })
      toast$('✅ Admin account created')
    } catch(e) { toast$('⚠️ '+e.message) }
    finally    { setAdminSaving(false) }
  }

  const TABS = [
    { id:'details',      icon:'🏫', label:'Details'      },
    { id:'admin',        icon:'👤', label:'Admin'        },
    { id:'subscription', icon:'📋', label:'Subscription' },
    { id:'invoices',     icon:'🧾', label:'Invoices'     },
  ]

  return (
    <Modal title={school.name} subtitle={[school.city,school.state].filter(Boolean).join(', ')||'No location'} onClose={onClose} width={680}>
      <div style={{ display:'flex', gap:2, borderBottom:'2px solid var(--fog)', marginBottom:22 }}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{ padding:'8px 16px', background:'none', border:'none', borderBottom:`2.5px solid ${tab===t.id?'var(--navy)':'transparent'}`,
                     color:tab===t.id?'var(--navy)':'var(--mist)', fontSize:12, fontWeight:700, cursor:'pointer', marginBottom:-2, display:'flex', alignItems:'center', gap:5 }}>
            <span>{t.icon}</span><span>{t.label}</span>
          </button>
        ))}
      </div>

      {tab==='details' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <Field label="School Name"><Input value={data.name||''} onChange={v=>setData(p=>({...p,name:v}))} /></Field>
          <Field label="Status">
            <Select value={data.isActive?'active':'inactive'} onChange={v=>setData(p=>({...p,isActive:v==='active'}))}
              options={[{value:'active',label:'Active'},{value:'inactive',label:'Inactive'}]} />
          </Field>
          <Field label="City"><Input value={data.city||''} onChange={v=>setData(p=>({...p,city:v}))} /></Field>
          <Field label="State"><Input value={data.state||''} onChange={v=>setData(p=>({...p,state:v}))} /></Field>
          <Field label="Phone"><Input value={data.phone||''} onChange={v=>setData(p=>({...p,phone:v}))} /></Field>
          <Field label="Email"><Input type="email" value={data.email||''} onChange={v=>setData(p=>({...p,email:v}))} /></Field>
          <div style={{ gridColumn:'1/-1' }}><Field label="Address"><Input value={data.address||''} onChange={v=>setData(p=>({...p,address:v}))} /></Field></div>
          <div style={{ gridColumn:'1/-1', display:'flex', justifyContent:'flex-end' }}>
            <Btn onClick={saveDetails} disabled={saving}>{saving?'Saving…':'Save Changes'}</Btn>
          </div>
        </div>
      )}

      {tab==='admin' && (
        <div>
          {adminData ? (
            <>
              {/* Existing admin info */}
              <div style={{ background:'#EBF4FB', border:'1.5px solid #AED6F1', borderRadius:12, padding:'16px 20px', marginBottom:22, display:'flex', alignItems:'center', gap:14 }}>
                <Avatar name={adminData.name||'?'} color="#1A4A6E" size={48} />
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:800, fontSize:15, color:'var(--navy)', marginBottom:3 }}>{adminData.name}</div>
                  <div style={{ fontSize:12, color:'var(--mist)', marginBottom:6 }}>{adminData.email}</div>
                  <div style={{ display:'flex', gap:6 }}>
                    <Badge label="Admin" color="#1A4A6E" bg="#EBF4FB" />
                    <Badge label="Active" color="#1B6B45" bg="#EDF7F2" />
                  </div>
                </div>
              </div>

              <Divider label="Reset Admin Password" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:10, alignItems:'end' }}>
                <Field label="New Password">
                  <Input type="password" value={newAdminPw} onChange={setNewAdminPw} placeholder="Min 8 characters" />
                </Field>
                <div style={{ marginBottom:14 }}>
                  <Btn onClick={resetAdminPassword} disabled={adminSaving}>{adminSaving?'Resetting…':'Reset Password'}</Btn>
                </div>
              </div>
              <div style={{ fontSize:11, color:'var(--mist)', marginTop:-8 }}>
                💡 The admin will need to use the new password on their next login.
              </div>
            </>
          ) : (
            <>
              {/* No admin yet */}
              <div style={{ background:'#FFF8E1', border:'1.5px solid #FFD54F', borderRadius:10, padding:'12px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ fontSize:20 }}>⚠️</span>
                <div style={{ fontSize:13, color:'#7A5800' }}>No admin account has been created for this school yet.</div>
              </div>

              <Divider label="Create Admin Account" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ gridColumn:'1/-1' }}>
                  <Field label="Full Name" required>
                    <Input value={newAdminForm.name} onChange={v=>setNewAdminForm(p=>({...p,name:v}))} placeholder="e.g. School Admin" />
                  </Field>
                </div>
                <Field label="Email" required>
                  <Input type="email" value={newAdminForm.email} onChange={v=>setNewAdminForm(p=>({...p,email:v}))} placeholder="admin@school.com" />
                </Field>
                <Field label="Password" required>
                  <Input type="password" value={newAdminForm.password} onChange={v=>setNewAdminForm(p=>({...p,password:v}))} placeholder="Min 8 characters" />
                </Field>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
                <Btn onClick={createAdmin} disabled={adminSaving}>{adminSaving?'Creating…':'Create Admin Account'}</Btn>
              </div>
            </>
          )}
        </div>
      )}

      {tab==='subscription' && (
        <div>
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--mist)', textTransform:'uppercase', letterSpacing:0.7, marginBottom:8 }}>Current Status</div>
            <SubBanner sub={sub} />
          </div>
          {!sub && (
            <div style={{ textAlign:'center', padding:'28px 0 8px' }}>
              <Btn onClick={activateSub} disabled={saving}>{saving?'Activating…':'🚀 Activate Trial Subscription'}</Btn>
              <div style={{ fontSize:11, color:'var(--mist)', marginTop:10, lineHeight:1.6 }}>Creates a 30-day free trial. You can change the plan after activation.</div>
            </div>
          )}
          {sub && (
            <>
              <Divider label="Change Plan" />
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:20 }}>
                {PLANS.map(p=>(
                  <div key={p.id} onClick={()=>setSub(s=>({...s,plan:p.id}))}
                    style={{ border:`2px solid ${sub.plan===p.id?p.color:'var(--border)'}`, background:sub.plan===p.id?p.bg:'white',
                             borderRadius:9, padding:'10px 8px', cursor:'pointer', textAlign:'center', transition:'all .12s' }}>
                    <div style={{ fontWeight:800, fontSize:12, color:p.color }}>{p.label}</div>
                    <div style={{ fontSize:9, color:'var(--mist)', marginTop:3 }}>👨‍🏫 {p.teachers===999?'∞':p.teachers}</div>
                    <div style={{ fontSize:9, color:'var(--mist)' }}>👤 {p.students===999?'∞':p.students}</div>
                    <div style={{ fontSize:10, fontWeight:700, color:p.color, marginTop:3 }}>{p.price}</div>
                  </div>
                ))}
              </div>
              <Divider label="Subscription Details" />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                <Field label="Status">
                  <Select value={sub.status} onChange={v=>setSub(s=>({...s,status:v}))}
                    options={SUB_STATUSES.map(s=>({value:s,label:s.charAt(0).toUpperCase()+s.slice(1)}))} />
                </Field>
                <Field label="Annual Fee (₹)">
                  <Input type="number" value={sub.annualFee||''} onChange={v=>setSub(s=>({...s,annualFee:+v}))} placeholder="0" />
                </Field>
                <Field label="Valid From">
                  <Input type="date" value={sub.validFrom?sub.validFrom.split('T')[0]:''} onChange={v=>setSub(s=>({...s,validFrom:v}))} />
                </Field>
                <Field label="Valid Until">
                  <Input type="date" value={sub.validUntil?sub.validUntil.split('T')[0]:''} onChange={v=>setSub(s=>({...s,validUntil:v}))} />
                </Field>
                <div style={{ gridColumn:'1/-1' }}>
                  <Field label="Internal Notes">
                    <textarea value={sub.notes||''} onChange={e=>setSub(s=>({...s,notes:e.target.value}))}
                      placeholder="Billing notes, renewal contacts…"
                      style={{ width:'100%', minHeight:64, padding:'9px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:12, boxSizing:'border-box', resize:'vertical', outline:'none' }} />
                  </Field>
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end' }}>
                <Btn onClick={saveSub} disabled={saving}>{saving?'Saving…':'Update Subscription'}</Btn>
              </div>
            </>
          )}
        </div>
      )}

      {tab==='invoices' && <InvoicesTab schoolId={school._id} sub={sub} setSub={setSub} toast$={toast$} />}
    </Modal>
  )
}

// ── Invoices Tab ──────────────────────────────────────────────────────────────
function genInvoiceNumber(existingInvoices) {
  const yr  = new Date().getFullYear()
  const seq = String((existingInvoices||[]).length + 1).padStart(3,'0')
  return `INV-${seq}/${yr}`
}

function InvoicesTab({ schoolId, sub, setSub, toast$ }) {
  const api = useApi()
  const [showAdd, setShowAdd] = useState(false)
  const planPrice = PLAN_PRICES[sub?.plan] ?? 0
  const yr        = new Date().getFullYear()
  const autoDesc  = sub?.plan ? `Annual subscription ${yr}-${String(yr+1).slice(2)} (${sub.plan.charAt(0).toUpperCase()+sub.plan.slice(1)} plan)` : ''
  const [f,setF]  = useState({ invoiceNumber:genInvoiceNumber(sub?.invoices), amount:planPrice>0?String(planPrice):'', description:autoDesc, status:'pending' })
  const set = k => v => setF(p=>({...p,[k]:v}))

  const openAdd = () => {
    setF({ invoiceNumber:genInvoiceNumber(sub?.invoices), amount:planPrice>0?String(planPrice):'', description:autoDesc, status:'pending' })
    setShowAdd(true)
  }

  const invoices = (sub?.invoices||[]).slice().sort((a,b)=>new Date(b.date)-new Date(a.date))

  const addInvoice = async () => {
    if (!f.invoiceNumber||!f.amount) return toast$('⚠️ Invoice # and amount required')
    try {
      const d = await api('POST', `/subscriptions/${schoolId}/invoices`, f)
      setSub(d.subscription||d.data||d); setShowAdd(false); toast$('✅ Invoice added')
    } catch(e) { toast$('⚠️ '+e.message) }
  }

  const markPaid = async inv => {
    try {
      const d = await api('PATCH', `/subscriptions/${schoolId}/invoices/${inv._id}`, { status:'paid', paidOn:new Date().toISOString() })
      setSub(s=>({ ...s, invoices:s.invoices.map(i=>i._id===inv._id?(d.invoice||inv):i) }))
      toast$('✅ Marked as paid')
    } catch(e) { toast$('⚠️ '+e.message) }
  }

  const INV = { pending:{color:'#F9A825',bg:'#FFF9C4'}, paid:{color:'#1B6B45',bg:'#EDF7F2'}, overdue:{color:'#C0392B',bg:'#FDEDEC'} }
  const paidTotal = invoices.filter(i=>i.status==='paid').reduce((s,i)=>s+(+i.amount||0),0)

  return (
    <div>
      <div style={{ display:'flex', gap:10, marginBottom:14, alignItems:'center' }}>
        {invoices.length > 0 && (
          <>
            <div style={{ background:'#EDF7F2', border:'1.5px solid #52BE80', borderRadius:8, padding:'8px 16px', fontSize:12 }}>
              <span style={{ color:'var(--mist)', fontWeight:600 }}>Collected: </span>
              <span style={{ fontWeight:800, color:'#1B6B45' }}>₹{paidTotal.toLocaleString('en-IN')}</span>
            </div>
            {planPrice > 0 && (
              <div style={{ background:'#EBF4FB', border:'1.5px solid #AED6F1', borderRadius:8, padding:'8px 16px', fontSize:12 }}>
                <span style={{ color:'var(--mist)', fontWeight:600 }}>Plan rate: </span>
                <span style={{ fontWeight:800, color:'#1A4A6E' }}>₹{planPrice.toLocaleString('en-IN')}/yr</span>
              </div>
            )}
          </>
        )}
        <div style={{ flex:1 }} />
        <Btn small onClick={openAdd}>+ Add Invoice</Btn>
      </div>

      {showAdd && (
        <div style={{ background:'var(--fog)', borderRadius:10, padding:'14px 16px', marginBottom:16, border:'1.5px solid var(--border)' }}>
          <div style={{ fontWeight:700, fontSize:12, color:'var(--navy)', marginBottom:12 }}>New Invoice</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:8 }}>
            <Field label="Invoice #"><Input value={f.invoiceNumber} onChange={set('invoiceNumber')} placeholder="INV-001/2026" /></Field>
            <Field label="Amount (₹)"><Input type="number" value={f.amount} onChange={set('amount')} placeholder={String(planPrice||0)} /></Field>
            <Field label="Status">
              <Select value={f.status} onChange={set('status')} options={['pending','paid','overdue'].map(s=>({value:s,label:s.charAt(0).toUpperCase()+s.slice(1)}))} />
            </Field>
          </div>
          <Field label="Description"><Input value={f.description} onChange={set('description')} placeholder="Annual subscription…" /></Field>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:8 }}>
            <Btn small variant="secondary" onClick={()=>setShowAdd(false)}>Cancel</Btn>
            <Btn small onClick={addInvoice}>Save Invoice</Btn>
          </div>
        </div>
      )}

      {invoices.length===0
        ? <Empty icon="🧾" title="No invoices yet" sub="Add the first invoice to start tracking billing." />
        : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'var(--fog)' }}>
                {['Invoice #','Amount','Date','Status',''].map(h=>(
                  <th key={h} style={{ textAlign:'left', padding:'7px 12px', fontSize:10, fontWeight:700, color:'var(--mist)', textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv=>{
                const sc = INV[inv.status]||{}
                return (
                  <tr key={inv._id} style={{ borderBottom:'1px solid var(--fog)' }}>
                    <td style={{ padding:'9px 12px', fontSize:13 }}>{inv.invoiceNumber}</td>
                    <td style={{ padding:'9px 12px', fontSize:13, fontWeight:700, color:'var(--navy)' }}>₹{(+inv.amount).toLocaleString('en-IN')}</td>
                    <td style={{ padding:'9px 12px', fontSize:12, color:'var(--mist)' }}>{new Date(inv.date||inv.createdAt||Date.now()).toLocaleDateString('en-IN')}</td>
                    <td style={{ padding:'9px 12px' }}><Badge label={inv.status.toUpperCase()} color={sc.color} bg={sc.bg} /></td>
                    <td style={{ padding:'9px 12px', textAlign:'right' }}>
                      {inv.status==='pending' && <Btn small onClick={()=>markPaid(inv)}>Mark Paid</Btn>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )
      }
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// CLASSROOMS TAB — fully school-scoped
// ══════════════════════════════════════════════════════════════════════════════
function ClassroomsTab({ schoolId }) {
  const api = useApi()
  const [classrooms, setClassrooms] = useState([])
  const [loading,    setLoading]    = useState(false)
  const [error,      setError]      = useState('')
  const [showAdd,    setShowAdd]    = useState(false)
  const [selected,   setSelected]   = useState(null)
  const [toast,      setToast]      = useState('')
  const toast$ = msg => { setToast(msg); setTimeout(()=>setToast(''),3200) }

  const load = useCallback(async () => {
    if (!schoolId) { setClassrooms([]); return }
    setLoading(true); setError('')
    try {
      const d = await api('GET', `/classrooms?school=${schoolId}`)
      setClassrooms(pickArray(d,'classrooms','data','items'))
    } catch(e) { setError(e.message) }
    finally    { setLoading(false) }
  }, [api, schoolId])

  useEffect(() => { load() }, [load])

  return (
    <div>
      {!schoolId ? (
        <Empty icon="🏫" title="Select a school" sub="Choose a school above to view and manage its classrooms." />
      ) : (
        <>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ fontSize:13, color:'var(--mist)' }}>
              {classrooms.length} classroom{classrooms.length!==1?'s':''} · Click any card to assign teachers &amp; students
            </div>
            <Btn onClick={()=>setShowAdd(true)}>+ Add Classroom</Btn>
          </div>

          {loading ? <Spinner /> :
           error   ? <ErrPane msg={error} onRetry={load} /> :
           classrooms.length===0 ? <Empty icon="🏛️" title="No classrooms yet" sub="Create your first classroom to organise students and teachers." /> : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:12 }}>
              {classrooms.map(c=>(
                <div key={c._id} onClick={()=>setSelected(c)}
                  style={{ background:'white', borderRadius:12, border:'1.5px solid var(--border)', cursor:'pointer', overflow:'hidden', transition:'box-shadow .15s' }}
                  onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 20px rgba(0,0,0,.1)'}
                  onMouseLeave={e=>e.currentTarget.style.boxShadow='none'}>
                  <div style={{ background:'var(--navy)', padding:'12px 16px' }}>
                    <div style={{ fontWeight:800, fontSize:13, color:'white' }}>{c.name}</div>
                    <div style={{ fontSize:10, color:'rgba(255,255,255,.55)', marginTop:2 }}>
                      {[c.grade,c.section].filter(Boolean).join(' · ')||'No grade'}
                      {c.academicYear ? ` · ${c.academicYear}` : ''}
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1, background:'var(--border)' }}>
                    <div style={{ background:'white', padding:'12px', textAlign:'center' }}>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:900, color:'var(--navy)', lineHeight:1 }}>{c.studentCount||0}</div>
                      <div style={{ fontSize:9, color:'var(--mist)', fontWeight:700, marginTop:3 }}>STUDENTS</div>
                    </div>
                    <div style={{ background:'white', padding:'12px', textAlign:'center' }}>
                      <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:900, color:'#1B6B45', lineHeight:1 }}>{(c.teachers||[]).length}</div>
                      <div style={{ fontSize:9, color:'var(--mist)', fontWeight:700, marginTop:3 }}>TEACHERS</div>
                    </div>
                  </div>
                  <div style={{ padding:'8px 14px', fontSize:11, color:'#1A4A6E', fontWeight:600 }}>Click to manage →</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showAdd  && <AddClassroomModal schoolId={schoolId} onClose={()=>setShowAdd(false)} onSaved={()=>{ setShowAdd(false); load(); toast$('✅ Classroom created') }} />}
      {selected && <ClassroomDetailModal classroom={selected} schoolId={schoolId} onClose={()=>{ setSelected(null); load() }} toast$={toast$} />}
      <Toast msg={toast} />
    </div>
  )
}

function AddClassroomModal({ schoolId, onClose, onSaved }) {
  const api = useApi()
  const curYear = new Date().getFullYear()
  const [f,setF]   = useState({ name:'',grade:'',section:'',academicYear:`${curYear}-${String(curYear+1).slice(2)}`,description:'' })
  const [saving,setSaving] = useState(false)
  const [err,setErr]       = useState('')
  const set = k => v => setF(p=>({...p,[k]:v}))

  const submit = async () => {
    if (!f.name) return setErr('Classroom name is required.')
    setSaving(true)
    try { await api('POST', '/classrooms', {...f, school:schoolId}); onSaved() }
    catch(e) { setErr(e.message) }
    finally  { setSaving(false) }
  }

  return (
    <Modal title="Add Classroom" subtitle="Group students and teachers in a classroom" onClose={onClose}>
      <ErrMsg msg={err} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Classroom Name" required><Input value={f.name} onChange={set('name')} placeholder="e.g. Grade 1 - A" /></Field>
        </div>
        <Field label="Grade"><Input value={f.grade} onChange={set('grade')} placeholder="Grade 1, KG2…" /></Field>
        <Field label="Section"><Input value={f.section} onChange={set('section')} placeholder="A, B…" /></Field>
        <Field label="Academic Year"><Input value={f.academicYear} onChange={set('academicYear')} placeholder="2025-26" /></Field>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Notes (optional)"><Input value={f.description} onChange={set('description')} placeholder="Additional notes" /></Field>
        </div>
      </div>
      <div style={{ display:'flex', gap:9, justifyContent:'flex-end', marginTop:14 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving?'Creating…':'Create Classroom'}</Btn>
      </div>
    </Modal>
  )
}

function ClassroomDetailModal({ classroom, schoolId, onClose, toast$ }) {
  const api = useApi()
  const [detail,      setDetail]      = useState(null)
  const [allTeachers, setAllTeachers] = useState([])
  const [allStudents, setAllStudents] = useState([])
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState('')
  const [tSearch,     setTSearch]     = useState('')
  const [sSearch,     setSSearch]     = useState('')
  const [toggling,    setToggling]    = useState(null)

  useEffect(()=>{
    async function load() {
      try {
        const schoolQ = schoolId ? `&school=${schoolId}` : ''
        const [cr, teachers, students] = await Promise.all([
          api('GET', `/classrooms/${classroom._id}`),
          api('GET', `/users?role=teacher${schoolQ}`),
          api('GET', `/children?school=${schoolId||''}`),
        ])
        setDetail(cr.classroom||cr.data||cr)
        setAllTeachers(pickArray(teachers,'users','data','items'))
        setAllStudents(pickArray(students,'children','data','items'))
      } catch(e) { setError(e.message) }
      finally    { setLoading(false) }
    }
    load()
  }, [classroom._id, schoolId])

  const assignedTIds = new Set((detail?.teachers||[]).map(t=>String(t._id||t)))
  const assignedSIds = new Set((detail?.students||[]).map(s=>String(s._id||s)))

  const toggleTeacher = async uid => {
    setToggling(uid)
    try {
      const isIn = assignedTIds.has(String(uid))
      const d    = await api('POST', `/classrooms/${classroom._id}/teachers`, isIn?{remove:[uid]}:{add:[uid]})
      setDetail(d.classroom||d.data||d)
      toast$(isIn?'↩ Teacher removed':'✅ Teacher assigned')
    } catch(e) { toast$('⚠️ '+e.message) }
    finally    { setToggling(null) }
  }

  const toggleStudent = async sid => {
    setToggling(sid)
    try {
      const isIn = assignedSIds.has(String(sid))
      const d    = await api('POST', `/classrooms/${classroom._id}/students`, isIn?{remove:[sid]}:{add:[sid]})
      setDetail(prev => ({ ...prev, students: d.students||d.data||[] }))
      toast$(isIn?'↩ Student removed':'✅ Student assigned')
    } catch(e) { toast$('⚠️ '+e.message) }
    finally    { setToggling(null) }
  }

  if (loading) return <Modal title={classroom.name} onClose={onClose}><Spinner /></Modal>
  if (error)   return <Modal title={classroom.name} onClose={onClose}><ErrPane msg={error} /></Modal>

  const tFiltered   = allTeachers.filter(t=>!tSearch||t.name.toLowerCase().includes(tSearch.toLowerCase()))
  const sFiltered   = allStudents.filter(s=>!sSearch||s.name.toLowerCase().includes(sSearch.toLowerCase()))
  const tAssigned   = tFiltered.filter(t=> assignedTIds.has(String(t._id)))
  const tUnassigned = tFiltered.filter(t=>!assignedTIds.has(String(t._id)))
  const sAssigned   = sFiltered.filter(s=> assignedSIds.has(String(s._id)))
  const sUnassigned = sFiltered.filter(s=>!assignedSIds.has(String(s._id)))

  return (
    <Modal title={`🏛️ ${classroom.name}`}
           subtitle={`${assignedTIds.size} teacher${assignedTIds.size!==1?'s':''} · ${assignedSIds.size} student${assignedSIds.size!==1?'s':''} assigned`}
           onClose={onClose} width={740}>
      <div style={{ background:'#EBF4FB', border:'1px solid #AED6F1', borderRadius:8, padding:'9px 14px', marginBottom:18, fontSize:12, color:'#1A4A6E', display:'flex', alignItems:'center', gap:8 }}>
        <span style={{ fontSize:16 }}>💡</span>
        <span>Click any person to assign or remove them. Changes save instantly.</span>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <AssignColumn label="Teachers" icon="👨‍🏫" assigned={tAssigned} unassigned={tUnassigned}
          search={tSearch} onSearch={setTSearch} onToggle={toggleTeacher} toggling={toggling}
          activeColor="#1B6B45" activeBg="#EDF7F2" activeBorder="#52BE80"
          getSubtitle={t=>t.email||'—'} emptyMsg="No teachers in this school yet." />
        <AssignColumn label="Students" icon="👤" assigned={sAssigned} unassigned={sUnassigned}
          search={sSearch} onSearch={setSSearch} onToggle={toggleStudent} toggling={toggling}
          activeColor="#1A4A6E" activeBg="#EBF4FB" activeBorder="#4A90D9"
          getSubtitle={s=>(s.disabilities||[]).slice(0,2).join(', ')||'—'} emptyMsg="No students in this school yet." />
      </div>
    </Modal>
  )
}

function AssignColumn({ label, icon, assigned, unassigned, search, onSearch, onToggle, toggling, activeColor, activeBg, activeBorder, getSubtitle, emptyMsg }) {
  const total = assigned.length + unassigned.length
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:10 }}>
        <span style={{ fontSize:16 }}>{icon}</span>
        <span style={{ fontWeight:800, fontSize:13, color:'var(--navy)' }}>{label}</span>
        <span style={{ background:activeColor, color:'white', fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10 }}>{assigned.length}/{total} assigned</span>
      </div>
      <input value={search} onChange={e=>onSearch(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`}
        style={{ width:'100%', padding:'7px 10px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:11, outline:'none', marginBottom:10, boxSizing:'border-box' }} />
      {total===0
        ? <div style={{ background:'var(--fog)', borderRadius:8, padding:'20px 14px', textAlign:'center', fontSize:12, color:'var(--mist)', lineHeight:1.6 }}>{emptyMsg}</div>
        : (
          <div style={{ maxHeight:360, overflowY:'auto', display:'flex', flexDirection:'column', gap:4 }}>
            {assigned.length > 0 && (
              <>
                <div style={{ fontSize:9, fontWeight:800, color:activeColor, textTransform:'uppercase', letterSpacing:0.8, padding:'3px 2px' }}>✓ Assigned</div>
                {assigned.map(item=>(
                  <AssignRow key={item._id} item={item} isAssigned subtitle={getSubtitle(item)}
                    onToggle={onToggle} toggling={toggling} activeColor={activeColor} activeBg={activeBg} activeBorder={activeBorder} />
                ))}
                {unassigned.length > 0 && <div style={{ height:1, background:'var(--border)', margin:'6px 0' }} />}
              </>
            )}
            {unassigned.length > 0 && (
              <>
                <div style={{ fontSize:9, fontWeight:800, color:'var(--mist)', textTransform:'uppercase', letterSpacing:0.8, padding:'3px 2px' }}>+ Not assigned</div>
                {unassigned.map(item=>(
                  <AssignRow key={item._id} item={item} isAssigned={false} subtitle={getSubtitle(item)}
                    onToggle={onToggle} toggling={toggling} activeColor={activeColor} activeBg={activeBg} activeBorder={activeBorder} />
                ))}
              </>
            )}
          </div>
        )
      }
    </div>
  )
}

function AssignRow({ item, isAssigned, subtitle, onToggle, toggling, activeColor, activeBg, activeBorder }) {
  const busy = toggling === item._id
  return (
    <div onClick={()=>!busy&&onToggle(item._id)}
      style={{ display:'flex', alignItems:'center', gap:9, padding:'9px 11px', borderRadius:9,
               background:isAssigned?activeBg:'white', border:`1.5px solid ${isAssigned?activeBorder:'var(--border)'}`,
               cursor:busy?'wait':'pointer', opacity:busy?0.5:1, transition:'all .13s' }}>
      <Avatar name={item.name} color={isAssigned?activeColor:'#CBD5E0'} size={32} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12, fontWeight:700, color:'var(--ink)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name}</div>
        <div style={{ fontSize:10, color:'var(--mist)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{subtitle}</div>
      </div>
      <div style={{ width:26, height:26, borderRadius:'50%', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center',
                    background:isAssigned?activeColor:'var(--fog)', color:isAssigned?'white':'var(--mist)', fontSize:13, fontWeight:900,
                    border:`1.5px solid ${isAssigned?activeColor:'var(--border)'}` }}>
        {busy?'…':isAssigned?'✓':'+'}
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// TEACHERS TAB — fully school-scoped
// ══════════════════════════════════════════════════════════════════════════════
function TeachersTab({ schoolId }) {
  const api = useApi()
  const [users,   setUsers]   = useState([])
  const [sub,     setSub]     = useState(null)
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [editU,   setEditU]   = useState(null)
  const [search,  setSearch]  = useState('')
  const [toast,   setToast]   = useState('')
  const toast$ = msg => { setToast(msg); setTimeout(()=>setToast(''),3200) }

  useEffect(() => { setSearch('') }, [schoolId])

  const load = useCallback(async () => {
    if (!schoolId) { setUsers([]); setSub(null); return }
    setLoading(true); setError('')
    try {
      const [usersRes, subRes] = await Promise.allSettled([
        api('GET', `/users?role=teacher&school=${schoolId}`),
        api('GET', `/subscriptions/${schoolId}`),
      ])
      if (usersRes.status === 'fulfilled') setUsers(pickArray(usersRes.value,'users','data','items'))
      else throw new Error(usersRes.reason?.message || 'Failed to load teachers')
      if (subRes.status === 'fulfilled' && subRes.value) setSub(subRes.value.subscription||subRes.value.data||null)
    } catch(e) { setError(e.message) }
    finally    { setLoading(false) }
  }, [api, schoolId])

  useEffect(() => { load() }, [load])

  const deactivate = async uid => {
    if (!confirm('Deactivate this teacher?')) return
    try { await api('DELETE',`/users/${uid}`); load(); toast$('Teacher deactivated') }
    catch(e) { toast$('⚠️ '+e.message) }
  }
  const resetPw = async uid => {
    const pw = prompt('New password (min 8 chars):')
    if (!pw||pw.length<8) return
    try { await api('POST',`/users/${uid}/reset-password`,{newPassword:pw}); toast$('✅ Password reset') }
    catch(e) { toast$('⚠️ '+e.message) }
  }

  const activeCount  = users.filter(u => u.isActive).length
  const planObj      = PLANS.find(p => p.id === sub?.plan)
  const maxTeachers  = sub ? (planObj?.teachers ?? sub.maxTeachers ?? null) : null
  const filtered     = users.filter(u=>!search||u.name.toLowerCase().includes(search.toLowerCase())||u.email?.toLowerCase().includes(search.toLowerCase()))
  const atLimit      = maxTeachers !== null && activeCount >= maxTeachers && maxTeachers !== 999

  return (
    <div>
      {!schoolId ? (
        <Empty icon="🏫" title="Select a school" sub="Choose a school above to view and manage its teachers." />
      ) : (
        <>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍  Search teachers…"
              style={{ padding:'8px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:12, outline:'none', width:200 }} />
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:11, color:'var(--mist)' }}>{users.length} total ({users.filter(u=>!u.isActive).length} inactive)</span>
              {atLimit
                ? <span style={{ fontSize:11, fontWeight:700, color:'#C0392B', background:'#FDEDEC', padding:'5px 12px', borderRadius:8 }}>🔒 Teacher limit reached</span>
                : <Btn onClick={()=>setShowAdd(true)}>+ Add Teacher</Btn>
              }
            </div>
          </div>

          {!loading && <SeatBar used={activeCount} max={maxTeachers} label="Teachers" icon="👨‍🏫" />}

          {loading ? <Spinner /> :
           error   ? <ErrPane msg={error} onRetry={load} /> :
           filtered.length===0 ? <Empty icon="👨‍🏫" title="No teachers yet" sub="Add your first teacher to get started." /> : (
            <div style={{ background:'white', borderRadius:12, border:'1.5px solid var(--border)', overflow:'hidden' }}>
              {filtered.map((u,i)=>(
                <div key={u._id} style={{ display:'flex', alignItems:'center', gap:14, padding:'13px 16px', borderBottom:i<filtered.length-1?'1px solid var(--fog)':'none' }}>
                  <Avatar name={u.name} color="#1B6B45" size={40} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'var(--ink)', marginBottom:3 }}>{u.name}</div>
                    <div style={{ fontSize:11, color:'var(--mist)' }}>{u.email}</div>
                  </div>
                  <div style={{ display:'flex', gap:4, flexWrap:'wrap', maxWidth:200 }}>
                    {(u.classrooms||[]).length===0
                      ? <span style={{ fontSize:11, color:'var(--mist)', fontStyle:'italic' }}>No classroom assigned</span>
                      : (u.classrooms||[]).map(c=><Badge key={c._id||c} label={c.name||c} color="var(--navy)" bg="var(--fog)" />)
                    }
                  </div>
                  <Badge label={u.isActive?'Active':'Inactive'} color={u.isActive?'#1B6B45':'#9E9E9E'} bg={u.isActive?'#EDF7F2':'#F5F5F5'} />
                  <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                    <Btn small variant="secondary" onClick={()=>setEditU(u)}>Edit</Btn>
                    <Btn small variant="secondary" onClick={()=>resetPw(u._id)}>↺ PW</Btn>
                    {u.isActive && <Btn small variant="danger" onClick={()=>deactivate(u._id)}>Remove</Btn>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showAdd && <AddUserModal role="teacher" schoolId={schoolId} onClose={()=>setShowAdd(false)} onSaved={()=>{ setShowAdd(false); load(); toast$('✅ Teacher added') }} />}
      {editU   && <EditUserModal user={editU} onClose={()=>setEditU(null)} onSaved={()=>{ setEditU(null); load(); toast$('✅ Updated') }} toast$={toast$} />}
      <Toast msg={toast} />
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// STUDENTS TAB — fully school-scoped
// ══════════════════════════════════════════════════════════════════════════════
function StudentsTab({ schoolId }) {
  const api = useApi()
  const { getAuthHeader } = useAuth()
  const [students, setStudents] = useState([])
  const [sub,      setSub]      = useState(null)
  const [records,  setRecords]  = useState({})
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const [search,   setSearch]   = useState('')
  const [showAdd,  setShowAdd]  = useState(false)
  const [parentChild, setParentChild] = useState(null)
  const [toast,    setToast]    = useState('')
  const toast$ = msg => { setToast(msg); setTimeout(()=>setToast(''), 3200) }

  useEffect(() => { setSearch('') }, [schoolId])

  const load = useCallback(async () => {
    if (!schoolId) { setStudents([]); setSub(null); setRecords({}); return }
    setLoading(true); setError('')
    try {
      const [kidsRes, subRes] = await Promise.allSettled([
        api('GET', `/children?school=${schoolId}`),
        api('GET', `/subscriptions/${schoolId}`),
      ])
      const kids = kidsRes.status==='fulfilled' ? pickArray(kidsRes.value,'children','data','items') : []
      setStudents(kids)
      if (subRes.status==='fulfilled'&&subRes.value) setSub(subRes.value.subscription||subRes.value.data||null)
      if (kids.length) {
        const results = await Promise.allSettled(
          kids.map(k => fetch(`/api/progress/${k._id}`,{headers:getAuthHeader()}).then(r=>r.json()).then(d=>d.record?{id:k._id,record:d.record}:null))
        )
        const map = {}
        results.forEach(r=>{ if(r.status==='fulfilled'&&r.value) map[r.value.id]=r.value.record })
        setRecords(map)
      }
    } catch(e) { setError(e.message) }
    finally    { setLoading(false) }
  }, [api, schoolId, getAuthHeader])

  useEffect(() => { load() }, [load])

  const planObj     = PLANS.find(p => p.id === sub?.plan)
  const maxStudents = sub ? (planObj?.students ?? sub.maxStudents ?? null) : null
  const filtered    = students.filter(s=>!search||s.name.toLowerCase().includes(search.toLowerCase()))
  const atLimit     = maxStudents !== null && students.length >= maxStudents && maxStudents !== 999

  return (
    <div>
      {!schoolId ? (
        <Empty icon="🏫" title="Select a school" sub="Choose a school above to view and manage its students." />
      ) : (
        <>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍  Search students…"
              style={{ padding:'8px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:12, outline:'none', width:200 }} />
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:13, color:'var(--mist)' }}>{students.length} total</span>
              {atLimit
                ? <span style={{ fontSize:11, fontWeight:700, color:'#C0392B', background:'#FDEDEC', padding:'5px 12px', borderRadius:8 }}>🔒 Student limit reached</span>
                : <Btn onClick={()=>setShowAdd(true)}>+ Add Student</Btn>
              }
            </div>
          </div>

          {!loading && <SeatBar used={students.length} max={maxStudents} label="Students" icon="👤" />}

          {loading ? <Spinner /> :
           error   ? <ErrPane msg={error} onRetry={load} /> :
           filtered.length===0
             ? <Empty icon="👤" title="No students yet" sub="Add your first student using the button above." />
             : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:10 }}>
                {filtered.map(s=>{
                  const age = s.dob?Math.max(0,new Date().getFullYear()-new Date(s.dob).getFullYear()):null
                  const rec = records[s._id]
                  const scores  = domainScores(rec?.indicators)
                  const overall = overallScore(scores)
                  return (
                    <div key={s._id} style={{ background:'white', borderRadius:12, border:'1.5px solid var(--border)', overflow:'hidden' }}>
                      <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--fog)', display:'flex', alignItems:'center', gap:10 }}>
                        <Avatar name={s.name} color={s.color||'#1A4A6E'} size={38} />
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:700, fontSize:13, color:'var(--ink)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.name}</div>
                          <div style={{ fontSize:10, color:'var(--mist)' }}>
                            {age!=null?`Age ${age}`:'No DOB'}
                            {s.classroom?.name
                              ? <span style={{ marginLeft:4 }}>· 🏛️ {s.classroom.name}</span>
                              : <span style={{ marginLeft:4, color:'#E65100' }}>· ⚠️ No classroom</span>
                            }
                          </div>
                        </div>
                        {overall!==null&&(
                          <div style={{ textAlign:'center', flexShrink:0 }}>
                            <div style={{ fontSize:17, fontWeight:900, color:scoreColor(overall), lineHeight:1 }}>{overall}%</div>
                            <div style={{ fontSize:9, color:'var(--mist)', marginTop:2 }}>Overall</div>
                          </div>
                        )}
                      </div>
                      <div style={{ padding:'10px 14px' }}>
                        {overall!==null
                          ? DOMAIN_IDS.map(d=>scores[d]!=null&&(
                              <div key={d} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                                <div style={{ width:54, fontSize:9, color:'var(--mist)', flexShrink:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{DOMAIN_SHORT[d]}</div>
                                <div style={{ flex:1, height:5, background:'var(--fog)', borderRadius:3 }}>
                                  <div style={{ width:`${scores[d]}%`, height:'100%', background:DOMAIN_COLORS[d], borderRadius:3, transition:'width .4s' }} />
                                </div>
                                <div style={{ fontSize:9, color:'var(--mist)', width:24, textAlign:'right', flexShrink:0 }}>{scores[d]}%</div>
                              </div>
                            ))
                          : <div style={{ fontSize:11, color:'var(--mist)', fontStyle:'italic' }}>No progress data yet</div>
                        }
                        {(s.disabilities||[]).length>0&&(
                          <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:8 }}>
                            {(s.disabilities||[]).map(d=><Badge key={d} label={d} color="var(--navy)" bg="var(--fog)" />)}
                          </div>
                        )}
                      </div>
                      <div style={{ borderTop:'1px solid var(--fog)', padding:'8px 14px', display:'flex', justifyContent:'flex-end' }}>
                        <button onClick={()=>setParentChild(s)}
                          style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 12px', borderRadius:7,
                                   fontSize:11, fontWeight:700, cursor:'pointer', background:'white', border:'1.5px solid #1A4A6E', color:'#1A4A6E' }}>
                          🔑 Parent Access
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          }
        </>
      )}

      {showAdd && <AddStudentModal schoolId={schoolId} onClose={()=>setShowAdd(false)} onSaved={()=>{ setShowAdd(false); load(); toast$('✅ Student added') }} />}
      {parentChild && <ParentAccessModal child={parentChild} onClose={()=>setParentChild(null)} />}
      <Toast msg={toast} />
    </div>
  )
}

// ── Add Student Modal ─────────────────────────────────────────────────────────
const DISABILITY_OPTIONS = [
  'Intellectual Disability','Cerebral Palsy','Autism Spectrum Disorder',
  'Hearing Impairment','Visual Impairment','Down Syndrome',
  'Speech & Language Disorder','Learning Disability','Multiple Disabilities',
]

function AddStudentModal({ schoolId, onClose, onSaved }) {
  const api = useApi()
  const [f, setF] = useState({ name:'', dob:'', gender:'', disabilities:[], notes:'' })
  const [saving, setSaving] = useState(false)
  const [err,    setErr]    = useState('')
  const set = k => v => setF(p => ({ ...p, [k]: v }))

  const toggleDisability = d => setF(p => ({
    ...p, disabilities: p.disabilities.includes(d) ? p.disabilities.filter(x=>x!==d) : [...p.disabilities, d]
  }))

  const submit = async () => {
    if (!f.name.trim()) return setErr('Student name is required.')
    setSaving(true); setErr('')
    try { await api('POST', '/children', { ...f, school: schoolId }); onSaved() }
    catch(e) { setErr(e.message) }
    finally  { setSaving(false) }
  }

  return (
    <Modal title="Add Student" subtitle="Student profile — linked to this school" onClose={onClose} width={560}>
      <ErrMsg msg={err} />
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div style={{ gridColumn:'1/-1' }}>
          <Field label="Full Name" required><Input value={f.name} onChange={set('name')} placeholder="e.g. Arjun Pillai" /></Field>
        </div>
        <Field label="Date of Birth"><Input type="date" value={f.dob} onChange={set('dob')} /></Field>
        <Field label="Gender">
          <Select value={f.gender} onChange={set('gender')} placeholder="Select…"
            options={[{value:'male',label:'Male'},{value:'female',label:'Female'},{value:'other',label:'Other'}]} />
        </Field>
      </div>
      <Field label="Disabilities / Conditions">
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:4 }}>
          {DISABILITY_OPTIONS.map(d => {
            const sel = f.disabilities.includes(d)
            return (
              <div key={d} onClick={()=>toggleDisability(d)}
                style={{ padding:'5px 11px', borderRadius:20, fontSize:11, fontWeight:600, cursor:'pointer',
                         border:`1.5px solid ${sel?'var(--navy)':'var(--border)'}`,
                         background:sel?'var(--navy)':'white', color:sel?'white':'var(--ink)', transition:'all .12s' }}>
                {d}
              </div>
            )
          })}
        </div>
      </Field>
      <Field label="Notes (optional)">
        <textarea value={f.notes} onChange={e=>set('notes')(e.target.value)} placeholder="Additional notes…"
          style={{ width:'100%', minHeight:56, padding:'9px 12px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:12, boxSizing:'border-box', resize:'vertical', outline:'none' }} />
      </Field>
      <div style={{ display:'flex', gap:9, justifyContent:'flex-end', marginTop:8 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving?'Adding…':'Add Student'}</Btn>
      </div>
    </Modal>
  )
}

// ─── Shared user modals ───────────────────────────────────────────────────────
function AddUserModal({ role, schoolId, onClose, onSaved }) {
  const api = useApi()
  const [f,setF]           = useState({ name:'',email:'',password:'' })
  const [saving,setSaving] = useState(false)
  const [err,setErr]       = useState('')
  const set = k => v => setF(p=>({...p,[k]:v}))

  const submit = async () => {
    if (!f.name||!f.email||!f.password) return setErr('All fields are required.')
    if (f.password.length<8) return setErr('Password must be at least 8 characters.')
    setSaving(true)
    try { await api('POST','/users',{...f,role,school:schoolId}); onSaved() }
    catch(e) { setErr(e.message) }
    finally  { setSaving(false) }
  }

  const rl = role.charAt(0).toUpperCase()+role.slice(1)
  return (
    <Modal title={`Add ${rl}`} subtitle={`This ${role} will be linked to the selected school`} onClose={onClose}>
      <ErrMsg msg={err} />
      <Field label="Full Name" required><Input value={f.name} onChange={set('name')} placeholder="e.g. Priya Nair" /></Field>
      <Field label="Email" required><Input type="email" value={f.email} onChange={set('email')} placeholder={`${role}@school.com`} /></Field>
      <Field label="Password" required><Input type="password" value={f.password} onChange={set('password')} placeholder="Min 8 characters" /></Field>
      <div style={{ display:'flex', gap:9, justifyContent:'flex-end', marginTop:14 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving?'Creating…':`Add ${rl}`}</Btn>
      </div>
    </Modal>
  )
}

function EditUserModal({ user, onClose, onSaved, toast$ }) {
  const api = useApi()
  const [name,setName] = useState(user.name)
  const [active,setAct] = useState(user.isActive)
  const [saving,setSav] = useState(false)

  const submit = async () => {
    setSav(true)
    try { await api('PUT',`/users/${user._id}`,{name,isActive:active}); onSaved() }
    catch(e) { toast$?.('⚠️ '+e.message) }
    finally  { setSav(false) }
  }

  return (
    <Modal title={`Edit: ${user.name}`} onClose={onClose}>
      <Field label="Full Name"><Input value={name} onChange={setName} /></Field>
      <Field label="Account Status">
        <Select value={active?'active':'inactive'} onChange={v=>setAct(v==='active')}
          options={[{value:'active',label:'Active'},{value:'inactive',label:'Inactive'}]} />
      </Field>
      <div style={{ display:'flex', gap:9, justifyContent:'flex-end', marginTop:14 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn onClick={submit} disabled={saving}>{saving?'Saving…':'Save Changes'}</Btn>
      </div>
    </Modal>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// BILLING TAB
// ══════════════════════════════════════════════════════════════════════════════
function BillingTab() {
  const api = useApi()
  const [subs,   setSubs]   = useState([])
  const [loading,setLoading]= useState(true)
  const [error,  setError]  = useState('')

  const load = useCallback(async ()=>{
    setLoading(true); setError('')
    try { setSubs(pickArray(await api('GET','/subscriptions'),'subscriptions','data','items')) }
    catch(e) { setError(e.message) }
    finally  { setLoading(false) }
  },[api])

  useEffect(()=>{ load() },[load])

  if (loading) return <Spinner />
  if (error)   return <ErrPane msg={error} onRetry={load} />

  const active          = subs.filter(s=>s.status==='active')
  const totalRevenue    = subs.reduce((sum,s)=>{
    const paid = (s.invoices||[]).filter(i=>i.status==='paid')
    return sum + paid.reduce((ps,i)=>ps+(+i.amount||0),0)
  }, 0)
  const planRateRevenue = active.reduce((sum,s)=>sum+(PLAN_PRICES[s.plan]||0),0)
  const displayRevenue  = totalRevenue > 0 ? totalRevenue : planRateRevenue
  const expiring        = subs.filter(s=>{ if(s.status!=='active'||!s.validUntil) return false; const d=(new Date(s.validUntil)-new Date())/86400000; return d>0&&d<30 })
  const byPlan          = PLANS.map(p=>({...p, count:subs.filter(x=>x.plan===p.id).length}))

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:22 }}>
        {[
          { icon:'🏫', val:subs.length,                                                                   label:'Total Schools',  color:'var(--navy)' },
          { icon:'🟢', val:active.length,                                                                 label:'Active',         color:'#1B6B45' },
          { icon:'🔴', val:subs.filter(s=>['expired','suspended','cancelled'].includes(s.status)).length, label:'Inactive',       color:'#C0392B' },
          { icon:'₹',  val:`₹${(displayRevenue/1000).toFixed(1)}K`,                                      label:'Annual Revenue', color:'#7B2D8B' },
        ].map(s=>(
          <div key={s.label} style={{ background:'white', borderRadius:12, padding:'16px', border:'1.5px solid var(--border)' }}>
            <div style={{ fontSize:24, marginBottom:8 }}>{s.icon}</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:900, color:s.color, lineHeight:1 }}>{s.val}</div>
            <div style={{ fontSize:11, color:'var(--mist)', fontWeight:600, marginTop:5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
        <div style={{ background:'white', borderRadius:12, padding:'16px 18px', border:'1.5px solid var(--border)' }}>
          <div style={{ fontWeight:800, fontSize:13, color:'var(--navy)', marginBottom:14 }}>Plan Distribution</div>
          {byPlan.map(p=>(
            <div key={p.id} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
              <Badge label={p.label} color={p.color} bg={p.bg} />
              <div style={{ flex:1, height:7, background:'var(--fog)', borderRadius:4 }}>
                <div style={{ width:subs.length?`${(p.count/subs.length)*100}%`:'0%', height:'100%', background:p.color, borderRadius:4, transition:'width .4s' }} />
              </div>
              <span style={{ fontSize:12, fontWeight:800, color:p.color, minWidth:20, textAlign:'right' }}>{p.count}</span>
            </div>
          ))}
        </div>
        <div style={{ background:'white', borderRadius:12, padding:'16px 18px', border:'1.5px solid var(--border)' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, fontWeight:800, fontSize:13, color:'var(--navy)', marginBottom:14 }}>
            Expiring Soon
            {expiring.length>0&&<Badge label={expiring.length} color="#C0392B" bg="#FDEDEC" />}
          </div>
          {expiring.length===0
            ? <div style={{ fontSize:12, color:'var(--mist)', textAlign:'center', padding:'16px 0' }}>✅ No subscriptions expiring in 30 days</div>
            : expiring.map(s=>{
                const days=Math.ceil((new Date(s.validUntil)-new Date())/86400000)
                return (
                  <div key={s._id} style={{ display:'flex', alignItems:'center', gap:9, padding:'7px 0', borderBottom:'1px solid var(--fog)' }}>
                    <div style={{ flex:1, fontSize:12, fontWeight:700 }}>{s.school?.name||'—'}</div>
                    <Badge label={`${days}d left`} color={days<7?'#C0392B':'#E65100'} bg={days<7?'#FDEDEC':'#FFF3E0'} />
                  </div>
                )
              })
          }
        </div>
      </div>

      <div style={{ background:'white', borderRadius:12, border:'1.5px solid var(--border)', overflow:'hidden' }}>
        <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--border)', fontWeight:800, fontSize:13, color:'var(--navy)' }}>All Subscriptions</div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'var(--fog)' }}>
              {['School','Plan','Status','Seats','Expires','Annual Fee'].map(h=>(
                <th key={h} style={{ textAlign:'left', padding:'8px 14px', fontSize:10, fontWeight:700, color:'var(--mist)', textTransform:'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {subs.map(s=>{
              const sm   = SUB_META[s.status]||SUB_META.trial
              const plan = PLANS.find(p=>p.id===s.plan)
              const daysLeft = s.validUntil?Math.ceil((new Date(s.validUntil)-new Date())/86400000):null
              const fee = s.annualFee > 0 ? s.annualFee : (PLAN_PRICES[s.plan]||0)
              const p = PLANS.find(x=>x.id===s.plan)
              const t  = p?(p.teachers===999?'∞':p.teachers):(s.maxTeachers===999?'∞':s.maxTeachers)
              const st = p?(p.students===999?'∞':p.students):(s.maxStudents===999?'∞':s.maxStudents)
              return (
                <tr key={s._id} style={{ borderBottom:'1px solid var(--fog)' }}>
                  <td style={{ padding:'10px 14px', fontSize:13, fontWeight:700 }}>{s.school?.name||'—'}</td>
                  <td style={{ padding:'10px 14px' }}>{plan&&<Badge label={plan.label} color={plan.color} bg={plan.bg} />}</td>
                  <td style={{ padding:'10px 14px' }}><Badge label={sm.label} color={sm.color} bg={sm.bg} dot={sm.dot} /></td>
                  <td style={{ padding:'10px 14px', fontSize:11, color:'var(--mist)' }}>👨‍🏫 {t} · 👤 {st}</td>
                  <td style={{ padding:'10px 14px', fontSize:11, color:daysLeft!==null&&daysLeft<30?'#C0392B':'var(--mist)', fontWeight:daysLeft!==null&&daysLeft<30?700:400 }}>
                    {s.validUntil?new Date(s.validUntil).toLocaleDateString('en-IN'):'—'}
                    {daysLeft!==null&&daysLeft>0&&daysLeft<30&&<span style={{ fontSize:10, marginLeft:4 }}>({daysLeft}d)</span>}
                  </td>
                  <td style={{ padding:'10px 14px', fontSize:12, fontWeight:700, color:'var(--navy)' }}>
                    {fee>0 ? <span>₹{fee.toLocaleString('en-IN')}{s.annualFee<=0&&<span style={{ fontSize:9, color:'var(--mist)', marginLeft:4 }}>(plan rate)</span>}</span> : '—'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT — loads schools list once for superadmin, passes down to scoped tabs
// ══════════════════════════════════════════════════════════════════════════════
export default function AdminPanel({ onNav }) {
  const { user } = useAuth()
  const api = useApi()
  const isSA     = user?.role === 'superadmin'
  const schoolId = user?.school || null

  // Superadmin: load all schools once so the single global picker can use them
  const [schools,  setSchools]  = useState([])
  const [schLoading, setSchLoading] = useState(false)

  useEffect(() => {
    if (!isSA) return
    setSchLoading(true)
    api('GET', '/schools')
      .then(d => setSchools(pickArray(d,'schools','data','items')))
      .catch(() => {})
      .finally(() => setSchLoading(false))
  }, [isSA])

  // Single school selection, lifted up here so it persists across Classrooms/Teachers/Students tabs
  const [scopedSchoolId, setScopedSchoolId] = useState('')

  const TABS = isSA
    ? [
        { id:'schools',    icon:'🏫', label:'Schools'    },
        { id:'classrooms', icon:'🏛️', label:'Classrooms' },
        { id:'teachers',   icon:'👨‍🏫', label:'Teachers'   },
        { id:'students',   icon:'👤',  label:'Students'   },
        { id:'billing',    icon:'💳', label:'Billing'    },
      ]
    : [
        { id:'classrooms', icon:'🏛️', label:'Classrooms' },
        { id:'teachers',   icon:'👨‍🏫', label:'Teachers'   },
        { id:'students',   icon:'👤',  label:'Students'   },
      ]

  const [activeTab, setActiveTab] = useState(isSA?'schools':'classrooms')

  const SCOPED_TABS      = ['classrooms','teachers','students']
  const showSchoolPicker = isSA && SCOPED_TABS.includes(activeTab)
  const effectiveSchoolId = isSA ? scopedSchoolId : schoolId

  if (!['superadmin','admin'].includes(user?.role)) {
    return (
      <div style={{ padding:80, textAlign:'center' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🔒</div>
        <div style={{ fontSize:18, fontWeight:700, color:'var(--navy)' }}>Admin access required</div>
      </div>
    )
  }

  return (
    <div style={{ padding:'28px 26px 80px', maxWidth:1200, margin:'0 auto' }}>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:900, color:'var(--navy)', marginBottom:5, lineHeight:1 }}>
          {isSA ? 'Platform Admin' : 'School Admin'}
        </div>
        <div style={{ fontSize:13, color:'var(--mist)' }}>
          {isSA ? 'Manage all schools, subscriptions, teachers and students' : 'Managing your school · classrooms, teachers and students'}
        </div>
      </div>

      <div style={{ display:'flex', gap:2, borderBottom:'2px solid var(--fog)', marginBottom: showSchoolPicker ? 14 : 22 }}>
        {TABS.map(tab=>(
          <button key={tab.id} onClick={()=>setActiveTab(tab.id)}
            style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 20px', background:'none', border:'none',
                     borderBottom:`3px solid ${activeTab===tab.id?'var(--navy)':'transparent'}`,
                     color:activeTab===tab.id?'var(--navy)':'var(--mist)',
                     fontSize:13, fontWeight:700, cursor:'pointer', marginBottom:-2, transition:'color .15s' }}>
            <span>{tab.icon}</span><span>{tab.label}</span>
          </button>
        ))}
      </div>

      {showSchoolPicker && <SchoolPicker schools={schools} selectedId={scopedSchoolId} onChange={setScopedSchoolId} />}

      <div style={{ background:'var(--fog)', borderRadius:14, padding:'22px 22px', minHeight:440 }}>
        {activeTab==='schools'    && <SchoolsTab />}
        {activeTab==='classrooms' && <ClassroomsTab schoolId={effectiveSchoolId} />}
        {activeTab==='teachers'   && <TeachersTab   schoolId={effectiveSchoolId} />}
        {activeTab==='students'   && <StudentsTab   schoolId={effectiveSchoolId} />}
        {activeTab==='billing'    && <BillingTab />}
      </div>

      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  )
}